import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Users, 
  Trash2, 
  Loader2, 
  Mail, 
  Phone, 
  Edit, 
  Save, 
  Building2, 
  User, 
  MapPin 
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLeads = () => {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Edit Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [editForm, setEditForm] = useState({
    first_name: "",
    last_name: "",
    business_name: "",
    email: "",
    telephone: "",
    business_category: "",
    business_address: "",
    address_2: "",
    suite_number: "",
    city: "",
    state: "",
    zip_code: "",
    status: ""
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/admin/leads`, { headers });
      setLeads(response.data);
    } catch (error) {
      toast.error("Failed to fetch leads");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [token]);

  const handleDelete = async (e, leadId) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    
    try {
      await axios.delete(`${API}/admin/leads/${leadId}`, { headers });
      toast.success("Lead deleted");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const openEditModal = (lead) => {
    setEditingLead(lead);
    setEditForm({
      first_name: lead.first_name || "",
      last_name: lead.last_name || "",
      business_name: lead.business_name || "",
      email: lead.email || "",
      telephone: lead.telephone || "",
      business_category: lead.business_category || "",
      business_address: lead.business_address || "",
      address_2: lead.address_2 || "",
      suite_number: lead.suite_number || "",
      city: lead.city || "",
      state: lead.state || "",
      zip_code: lead.zip_code || "",
      status: lead.status || "new"
    });
    setEditModalOpen(true);
  };

  const handleSave = async () => {
    if (!editingLead) return;

    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/leads/${editingLead.id}`, editForm, { headers });
      toast.success("Lead updated successfully");
      setEditModalOpen(false);
      setEditingLead(null);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update lead");
    } finally {
      setIsSaving(false);
    }
  };

  // Get full name from first_name/last_name or contact_name
  const getFullName = (lead) => {
    if (lead.first_name && lead.last_name) {
      return `${lead.first_name} ${lead.last_name}`;
    }
    return lead.contact_name || "-";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-leads">
      <Card className="bg-navy-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Users className="w-5 h-5 text-primary" />
            Leads ({leads.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {leads.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-400">Business</TableHead>
                    <TableHead className="text-slate-400">Contact</TableHead>
                    <TableHead className="text-slate-400">Phone</TableHead>
                    <TableHead className="text-slate-400">Email</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Date</TableHead>
                    <TableHead className="text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead) => (
                    <TableRow
                      key={lead.id}
                      className="border-slate-800 hover:bg-navy-800/50 cursor-pointer"
                      onClick={() => openEditModal(lead)}
                      data-testid={`lead-row-${lead.id}`}
                    >
                      <TableCell className="text-white font-medium">
                        {lead.business_name}
                      </TableCell>
                      <TableCell className="text-slate-300">
                        {getFullName(lead)}
                      </TableCell>
                      <TableCell className="text-slate-400">
                        {lead.telephone ? (
                          <span 
                            className="flex items-center gap-1 hover:text-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.location.href = `tel:${lead.telephone}`;
                            }}
                          >
                            <Phone className="w-3 h-3" />
                            {lead.telephone}
                          </span>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-300">{lead.email}</span>
                          {lead.email && (
                            <a
                              href={`mailto:${lead.email}`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/20 hover:bg-primary/30 text-primary transition-colors"
                              title={`Email ${lead.email}`}
                              data-testid={`email-btn-${lead.id}`}
                            >
                              <Mail className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={lead.converted_to_client ? "default" : "secondary"}
                          className={lead.converted_to_client 
                            ? "bg-green-500/20 text-green-400 border-green-500/30" 
                            : "bg-slate-700 text-slate-300"
                          }
                        >
                          {lead.converted_to_client ? "Converted" : "Active"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-400 text-sm">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModal(lead);
                            }}
                            className="text-slate-400 hover:text-primary"
                            data-testid={`edit-lead-${lead.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDelete(e, lead.id)}
                            className="text-slate-400 hover:text-red-400"
                            data-testid={`delete-lead-${lead.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No leads yet</p>
          )}
        </CardContent>
      </Card>

      {/* Edit Lead Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-2xl bg-navy-900 border-slate-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary" />
              Edit Lead
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Update lead information for {editingLead?.business_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Contact Information */}
            <div className="space-y-4">
              <p className="text-sm text-slate-400 flex items-center gap-2 font-medium">
                <User className="w-4 h-4" />
                Contact Information
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">First Name</Label>
                  <Input
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                    className="bg-navy-950 border-slate-700 text-white"
                    data-testid="edit-lead-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Last Name</Label>
                  <Input
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                    className="bg-navy-950 border-slate-700 text-white"
                    data-testid="edit-lead-last-name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="bg-navy-950 border-slate-700 text-white"
                    data-testid="edit-lead-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Phone</Label>
                  <Input
                    type="tel"
                    value={editForm.telephone}
                    onChange={(e) => setEditForm({ ...editForm, telephone: e.target.value })}
                    className="bg-navy-950 border-slate-700 text-white"
                    data-testid="edit-lead-phone"
                  />
                </div>
              </div>
            </div>

            {/* Business Information */}
            <div className="space-y-4 pt-4 border-t border-slate-700">
              <p className="text-sm text-slate-400 flex items-center gap-2 font-medium">
                <Building2 className="w-4 h-4" />
                Business Information
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Business Name</Label>
                  <Input
                    value={editForm.business_name}
                    onChange={(e) => setEditForm({ ...editForm, business_name: e.target.value })}
                    className="bg-navy-950 border-slate-700 text-white"
                    data-testid="edit-lead-business-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Business Category</Label>
                  <Input
                    value={editForm.business_category}
                    onChange={(e) => setEditForm({ ...editForm, business_category: e.target.value })}
                    className="bg-navy-950 border-slate-700 text-white"
                    data-testid="edit-lead-business-category"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger className="bg-navy-950 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-900 border-slate-700">
                    <SelectItem value="new" className="text-white hover:bg-navy-800">New</SelectItem>
                    <SelectItem value="qualified" className="text-white hover:bg-navy-800">Qualified</SelectItem>
                    <SelectItem value="evaluation_complete" className="text-white hover:bg-navy-800">Evaluation Complete</SelectItem>
                    <SelectItem value="registered_user" className="text-white hover:bg-navy-800">Registered User</SelectItem>
                    <SelectItem value="converted" className="text-white hover:bg-navy-800">Converted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-4 pt-4 border-t border-slate-700">
              <p className="text-sm text-slate-400 flex items-center gap-2 font-medium">
                <MapPin className="w-4 h-4" />
                Address
              </p>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Street Address</Label>
                  <Input
                    value={editForm.business_address}
                    onChange={(e) => setEditForm({ ...editForm, business_address: e.target.value })}
                    placeholder="123 Main Street"
                    className="bg-navy-950 border-slate-700 text-white"
                    data-testid="edit-lead-address"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Address Line 2</Label>
                    <Input
                      value={editForm.address_2}
                      onChange={(e) => setEditForm({ ...editForm, address_2: e.target.value })}
                      placeholder="Building, Floor, etc."
                      className="bg-navy-950 border-slate-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Suite/Unit</Label>
                    <Input
                      value={editForm.suite_number}
                      onChange={(e) => setEditForm({ ...editForm, suite_number: e.target.value })}
                      placeholder="Suite 100"
                      className="bg-navy-950 border-slate-700 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">City</Label>
                    <Input
                      value={editForm.city}
                      onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                      placeholder="Denver"
                      className="bg-navy-950 border-slate-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">State</Label>
                    <Input
                      value={editForm.state}
                      onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                      placeholder="CO"
                      className="bg-navy-950 border-slate-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">ZIP Code</Label>
                    <Input
                      value={editForm.zip_code}
                      onChange={(e) => setEditForm({ ...editForm, zip_code: e.target.value })}
                      placeholder="80202"
                      className="bg-navy-950 border-slate-700 text-white"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-700">
            <Button
              variant="outline"
              onClick={() => setEditModalOpen(false)}
              className="border-slate-700 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-primary hover:bg-primary/90"
              data-testid="save-lead-btn"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLeads;
